<div class="plan-display-container">
  <!-- Header Row: Selectors + Billing Toggle - Show in 'cards' mode only -->
  @if (displayMode() === 'cards') {
    <!-- Mobile Header -->
    @if (isMobile()) {
      <div class="flex flex-col gap-4 mb-4 p-4 bg-brand-purple-50 rounded-xl">
        <!-- Billing Toggle -->
        <billing-toggle
          [value]="billingPeriod()"
          size="sm"
          (valueChange)="setBillingPeriod($event)">
        </billing-toggle>
        
        <!-- Selectors (stacked on mobile) -->
        @if (showRegionSelector() || showRangedValueSelector()) {
          <div class="flex flex-col gap-2">
            @if (showRegionSelector()) {
              <custom-select
                class="w-full"
                [options]="regionOptions()"
                [value]="selectedRegion()?.code"
                placeholder="Select Region"
                size="sm"
                (valueChange)="onRegionSelected($event)">
              </custom-select>
            }
            @if (showRangedValueSelector()) {
              <custom-select
                class="w-full"
                [options]="rangedValueOptions()"
                [value]="selectedRangedValue()"
                [placeholder]="rangedValueName()"
                size="sm"
                (valueChange)="onRangedValueSelected($event)">
              </custom-select>
            }
          </div>
        }
      </div>
    } @else {
      <!-- Desktop Header -->
      <div class="flex flex-wrap items-center justify-end gap-x-8 gap-y-2 mb-4 min-h-6">
        <p class="text-sm text-brand-purple-600 m-0 mr-auto whitespace-nowrap">* The initial price is displayed.</p>
        <!-- Region & Ranged Value Selectors (left side, if needed) -->
        @if (showRegionSelector() || showRangedValueSelector()) {
          <div class="flex items-center gap-3 mr-auto">
            @if (showRegionSelector()) {
              <div class="w-48">
                <custom-select
                  class="w-full"
                  [options]="regionOptions()"
                  [value]="selectedRegion()?.code"
                  placeholder="Select Region"
                  size="sm"
                  (valueChange)="onRegionSelected($event)">
                </custom-select>
              </div>
            }
            @if (showRangedValueSelector()) {
              <div class="w-60">
                <custom-select
                  class="w-full"
                  [options]="rangedValueOptions()"
                  [value]="selectedRangedValue()"
                  [placeholder]="rangedValueName()"
                  size="sm"
                  (valueChange)="onRangedValueSelected($event)">
                </custom-select>
              </div>
            }
          </div>
        }
        
        <!-- Billing Period Toggle (right aligned per Figma) -->
        <billing-toggle
          [value]="billingPeriod()"
          (valueChange)="setBillingPeriod($event)">
        </billing-toggle>
      </div>
    }
  }
  

  <!-- Plans Section -->
  @if (hasPlans()) {
    <!-- Comparison Mode -->
    @if (displayMode() === 'comparison') {
      @if (isMobile()) {
        <!-- Mobile Comparison Layout (per Figma 31-1718) -->
        <div class="flex flex-col gap-0.5">
          <!-- Sticky Header: Section name + Plan cards row -->
          <div class="sticky fix top-0 z-20 bg-white shadow-lg flex items-end gap-0.5"
            stickyClass="is-stuck" 
            [stickyHeightDiff]="140"
            [stickThreshold]="30"
            [unstickThreshold]="10">
            <!-- Left: Section Name -->
            <div class="w-[120px] flex-shrink-0 p-4 flex flex-col justify-end">
              <h3 class="text-sm font-bold text-base-700 leading-tight">
                {{ sectionName() || 'Payment models' }}
              </h3>
            </div>
            
            <!-- Right: Compact Plan Cards -->
            <div class="flex-1 bg-brand-purple-50 rounded-t-xl p-1 pt-1">
              <div class="flex gap-0.5">
                @for (plan of plansSig(); track plan.code) {
                  <plan-card
                    class="flex-1"
                    [plan]="plan"
                    [displayPrice]="getDisplayPrice(plan)"
                    [basePrice]="getBasePrice(plan)"
                    [priceDescriptionText]="getPriceDescriptionText(plan)"
                    [currencySymbol]="getCurrencySymbol(getDisplayCurrency(plan))"
                    [showPopularRing]="false"
                    [showSeatsInfo]="!!selectedRangedValue()"
                    [includedSeats]="getIncludedSeats()"
                    [rangedValueName]="rangedValueName()"
                    size="comparison-mobile"
                    [ctaLabel]="ctaLabel()"
                    [selected]="plan.code === highlightedPlanCode()"
                    (planSelected)="selectPlan($event)">
                  </plan-card>
                }
              </div>
            </div>
          </div>
        </div>
      } @else {
        <!-- Desktop Comparison Layout (per Figma node 1-2355) -->
        <div class="sticky top-0 z-20 bg-white pb-4 px-[60px]" 
          stickyClass="is-stuck" 
          [stickyHeightDiff]="140"
          [stickThreshold]="30"
          [unstickThreshold]="10">
          <div class="grid" 
            [style.grid-template-columns]="'424px repeat(' + plansSig().length + ', 1fr)'"
            [style.column-gap]="'0px'">
            <!-- Left Column: Info + Billing Toggle (424px) -->
            <div class="flex-shrink-0 rounded-2xl p-8 flex flex-col justify-end gap-3" style="padding-right: 24px;">
              <!-- Payment Models Header -->
              <h3 class="text-[24px] font-bold text-base-700 leading-tight mb-4 show-when-stuck">
                {{ sectionName() }}
              </h3>

              <h3 class="text-[24px] font-bold text-base-700 leading-tight hide-when-stuck">
                Payment models
              </h3>
              
              <!-- Billing Toggle -->
              <billing-toggle
                class="hide-when-stuck"
                [value]="billingPeriod()"
                size="sm"
                (valueChange)="setBillingPeriod($event)">
              </billing-toggle>            
              <!-- Region & Ranged Value Selectors -->
              @if ((showRegionSelector() || showRangedValueSelector())) {
                <div class="flex w-full gap-2 items-center justify-between hide-when-stuck">
                  @if (showRegionSelector()) {
                    <div class="w-full">
                      <label class="block text-xs font-medium text-gray-500 mb-1">Region</label>
                      <custom-select
                        class="w-full"
                        [options]="regionOptions()"
                        [value]="selectedRegion()?.code"
                        placeholder="Select Region"
                        size="xs"
                        (valueChange)="onRegionSelected($event)">
                      </custom-select>
                    </div>
                  }
                  @if (showRangedValueSelector()) {
                    <div class="w-full">
                      <label class="block text-xs font-medium text-gray-500 mb-1">{{ rangedValueName() }}</label>
                      <custom-select
                        class="w-full"
                        [options]="rangedValueOptions()"
                        [value]="selectedRangedValue()"
                        [placeholder]="rangedValueName()"
                        size="xs"
                        (valueChange)="onRangedValueSelected($event)">
                      </custom-select>
                    </div>
                  }
                </div>
              }
              

            </div>

            <!-- Plan Cards Grid (aligned with features-grid columns) -->
            @for (plan of plansSig(); track plan.code; let idx = $index) {
              <div class="bg-brand-purple-50 rounded-2xl p-4" [style.margin-left]="idx > 0 ? '2px' : '0'">
                <plan-card
                  [plan]="plan"
                  [displayPrice]="getDisplayPrice(plan)"
                  [basePrice]="getBasePrice(plan)"
                  [priceDescriptionText]="getPriceDescriptionText(plan)"
                  [currencySymbol]="getCurrencySymbol(getDisplayCurrency(plan))"
                  [showPopularRing]="showPopularBadge()"
                  [showSeatsInfo]="!!selectedRangedValue()"
                  [includedSeats]="getIncludedSeats()"
                  [rangedValueName]="rangedValueName()"
                  size="compact"
                  descriptionExtraClass="hide-when-stuck"
                  [ctaLabel]="ctaLabel()"
                    [selected]="plan.code === highlightedPlanCode()"
                    (planSelected)="selectPlan($event)">
                </plan-card>
              </div>
            }
          </div>
        </div><!-- close sticky wrapper -->
      }
    } @else {
      <!-- Cards Mode -->
      @if (isMobile()) {
        <!-- Mobile: Horizontal scrolling slider (per Figma 31-1202) -->
        <div class="overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
          <div class="flex gap-4" style="width: max-content;">
            @for (plan of plansSig(); track plan.code) {
              <plan-card
                class="w-80 flex-shrink-0"
                [plan]="plan"
                [displayPrice]="getDisplayPrice(plan)"
                [basePrice]="getBasePrice(plan)"
                [priceDescriptionText]="getPriceDescriptionText(plan)"
                [currencySymbol]="getCurrencySymbol(getDisplayCurrency(plan))"
                [showPopularRing]="showPopularBadge()"
                [showHighlights]="featuresMode() === 'highlighted'"
                [highlightedFeatures]="getPlanFeatures(plan)"
                [showSeatsInfo]="!!selectedRangedValue()"
                [includedSeats]="getIncludedSeats()"
                [rangedValueName]="rangedValueName()"
                [additionalSeatPrice]="getAdditionalSeatPrice(plan)"
                size="mobile"
                [ctaLabel]="ctaLabel()"
                    [selected]="plan.code === highlightedPlanCode()"
                    (planSelected)="selectPlan($event)">
              </plan-card>
            }
          </div>
        </div>
      } @else {
        <!-- Desktop: Standard grid layout -->
        <div [class]="gridColsClass()">
          @for (plan of plansSig(); track plan.code) {
            <plan-card
              [plan]="plan"
              [displayPrice]="getDisplayPrice(plan)"
              [basePrice]="getBasePrice(plan)"
              [priceDescriptionText]="getPriceDescriptionText(plan)"
              [currencySymbol]="getCurrencySymbol(getDisplayCurrency(plan))"
              [showPopularRing]="showPopularBadge()"
              [showHighlights]="featuresMode() === 'highlighted'"
              [highlightedFeatures]="getPlanFeatures(plan)"
              [showSeatsInfo]="!!selectedRangedValue()"
              [includedSeats]="getIncludedSeats()"
              [rangedValueName]="rangedValueName()"
              [additionalSeatPrice]="getAdditionalSeatPrice(plan)"
              size="default"
              [ctaLabel]="ctaLabel()"
                    [selected]="plan.code === highlightedPlanCode()"
                    (planSelected)="selectPlan($event)">
            </plan-card>
          }
        </div>
      }
    }
  }

  <!-- Empty State -->
  @if (!hasPlans()) {
    <div class="text-center py-16">
      <div class="text-6xl mb-4">💳</div>
      <h4 class="text-xl font-semibold text-gray-900 mb-2">No Plans Available</h4>
      <p class="text-gray-600">Check back later for available pricing plans.</p>
    </div>
  }

  <!-- Region Info -->
  @if (selectedRegion()) {
    <div class="text-center mt-8 text-sm text-gray-500 hidden">
      Prices shown for {{ selectedRegion()?.name }} ({{ selectedRegion()?.currency }})
    </div>
  }

  <!-- Features Grid for Full Comparison Mode -->
  @if (isFullMode()) {
    @if (featureGroupsSig().length > 0 || ungroupedFeaturesSig().length > 0) {
      <div class="mt-0" 
        [ngClass]="isMobile() ? 'px-0' : 'px-4 lg:px-[60px]'">
        <features-grid
          [showPlanNames]="true"
          [compactMode]="true"
          [billingPeriod]="billingPeriod()"
          [showBillingToggle]="showBillingToggle()"
          [mode]="featuresMode()"
          [stickyOffset]="170"
          (onBillingToggle)="toggleBillingPeriod()"
          (onActiveSectionChange)="onActiveSectionChange($event)">
        </features-grid>
      </div>
    }
  }
</div>
